// src/components/tiptap/extensions/noteReadingGame/MusicNoteGame.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../../../../common/SafeIcon.jsx";
import { useSfx, useLevelMusic } from "./useAudioManager.js";

const { FiVolume2, FiVolumeX, FiRotateCcw, FiPause, FiPlay } = FiIcons;
const NOTE_KEY_EVENT = "mms:note-reading-key";

/** Note pools per clef (13 notes each, matching Treble span) */
const NOTES_BY_CLEF = {
  treble: [
    { id: "C4", name: "C", octave: 4, pos: -3 },
    { id: "D4", name: "D", octave: 4, pos: -2 },
    { id: "E4", name: "E", octave: 4, pos: -1 },
    { id: "F4", name: "F", octave: 4, pos: 0 },
    { id: "G4", name: "G", octave: 4, pos: 1 },
    { id: "A4", name: "A", octave: 4, pos: 2 },
    { id: "B4", name: "B", octave: 4, pos: 3 },
    { id: "C5", name: "C", octave: 5, pos: 4 },
    { id: "D5", name: "D", octave: 5, pos: 5 },
    { id: "E5", name: "E", octave: 5, pos: 6 },
    { id: "F5", name: "F", octave: 5, pos: 7 },
    { id: "G5", name: "G", octave: 5, pos: 8 },
    { id: "A5", name: "A", octave: 5, pos: 9 },
  ],
  bass: [
    { id: "C2", name: "C", octave: 2, pos: -5 },
    { id: "D2", name: "D", octave: 2, pos: -4 },
    { id: "E2", name: "E", octave: 2, pos: -3 },
    { id: "F2", name: "F", octave: 2, pos: -2 },
    { id: "G2", name: "G", octave: 2, pos: -1 },
    { id: "A2", name: "A", octave: 2, pos: 0 },
    { id: "B2", name: "B", octave: 2, pos: 1 },
    { id: "C3", name: "C", octave: 3, pos: 2 },
    { id: "D3", name: "D", octave: 3, pos: 3 },
    { id: "E3", name: "E", octave: 3, pos: 4 },
    { id: "F3", name: "F", octave: 3, pos: 5 },
    { id: "G3", name: "G", octave: 3, pos: 6 },
    { id: "A3", name: "A", octave: 3, pos: 7 },
  ],
  alto: [
    { id: "C3", name: "C", octave: 3, pos: -4 },
    { id: "D3", name: "D", octave: 3, pos: -3 },
    { id: "E3", name: "E", octave: 3, pos: -2 },
    { id: "F3", name: "F", octave: 3, pos: -1 },
    { id: "G3", name: "G", octave: 3, pos: 0 },
    { id: "A3", name: "A", octave: 3, pos: 1 },
    { id: "B3", name: "B", octave: 3, pos: 2 },
    { id: "C4", name: "C", octave: 4, pos: 3 },
    { id: "D4", name: "D", octave: 4, pos: 4 },
    { id: "E4", name: "E", octave: 4, pos: 5 },
    { id: "F4", name: "F", octave: 4, pos: 6 },
    { id: "G4", name: "G", octave: 4, pos: 7 },
    { id: "A4", name: "A", octave: 4, pos: 8 },
  ],
};

const CHOICES = ["C", "D", "E", "F", "G", "A", "B"];
const rand = (arr) => arr[Math.floor(Math.random() * arr.length)];

// Leveling knobs
const LEVEL_INTERVAL = 6;
const SPEED_BASE = 70;
const SPEED_GROWTH = 1.18;

/** Clef glyph config */
const CLEFS = {
  treble: {
    glyph: "𝄞",
    spaces: 8.3,
    x: 66,
    yAdjSpaces: 3.7,
    widthEm: 0.5,
    targetLineIndex: 1,
    baseline: "alphabetic",
  },
  bass: {
    glyph: "𝄢",
    spaces: 5.7,
    x: 66,
    yAdjSpaces: -0.54,
    widthEm: 0.52,
    targetLineIndex: 3,
    baseline: "middle",
  },
  alto: {
    glyph: "𝄡",
    spaces: 5.6,
    x: 66,
    yAdjSpaces: 0.0,
    widthEm: 0.54,
    targetLineIndex: 2,
    baseline: "central",
  },
};

function usePhoneOrientationGate() {
  const [state, setState] = useState(() => {
    if (typeof window === "undefined") return { isPhone: false, isPortrait: false };
    const w = window.innerWidth;
    const h = window.innerHeight;
    const isPortrait = h >= w;
    const coarse =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches;
    // "phone-ish" heuristic: coarse pointer + narrow-ish width
    const isPhone = coarse && Math.min(w, h) <= 480;
    return { isPhone, isPortrait };
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const compute = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const isPortrait = h >= w;
      const coarse =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(pointer: coarse)").matches;
      const isPhone = coarse && Math.min(w, h) <= 480;
      setState({ isPhone, isPortrait });
    };

    compute();

    window.addEventListener("resize", compute);
    window.addEventListener("orientationchange", compute);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("orientationchange", compute);
    };
  }, []);

  return state;
}

export default function MusicNoteGame({ compact = true, clef: initialClef = "treble" }) {
  // Orientation gate (phones in portrait)
  const { isPhone, isPortrait } = usePhoneOrientationGate();
  const [dismissRotateHint, setDismissRotateHint] = useState(false);
  const showRotateHint = isPhone && isPortrait && !dismissRotateHint;

  // Clef + notes
  const [clef, setClef] = useState(initialClef);
  useEffect(() => {
    if (!["treble", "bass", "alto"].includes(clef)) setClef("alto");
  }, [clef]);

  const [notesPool, setNotesPool] = useState(() => NOTES_BY_CLEF[initialClef]);
  useEffect(() => setNotesPool(NOTES_BY_CLEF[clef]), [clef]);

  // Game state
  const [target, setTarget] = useState(() => rand(notesPool));
  const targetRef = useRef(null);
  useEffect(() => {
    targetRef.current = target;
  }, [target]);

  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [lives, setLives] = useState(3);
  const [muted, setMuted] = useState(false);
  const [flash, setFlash] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | running | paused | over | levelup
  const [level, setLevel] = useState(1);
  const [correctCount, setCorrectCount] = useState(0);
  const [anim, setAnim] = useState(null);

  // Animation state
  const [noteX, setNoteX] = useState(null);
  const roundActiveRef = useRef(false);
  const rafRef = useRef(0);

  const sfx = useSfx();
  const music = useLevelMusic();

  // Dimensions & mapping
  const height = compact ? 160 : 240;
  const width = compact ? 560 : 720;
  const staffTopY = 30;
  const lineGap = 12;
  const posToY = (p) => staffTopY + (7 - p) * (lineGap / 2);

  // Clef metrics
  const clefCfg = CLEFS[clef];
  const CLEF_FONT_SIZE = clefCfg.spaces * lineGap;
  const CLEF_X = clefCfg.x;
  const anchorLineY = staffTopY + clefCfg.targetLineIndex * lineGap;
  const CLEF_Y = anchorLineY + clefCfg.yAdjSpaces * lineGap;
  const CLEF_VISUAL_WIDTH = CLEF_FONT_SIZE * clefCfg.widthEm;
  const NOTE_MIN_X = CLEF_X + CLEF_VISUAL_WIDTH - 4;

  const NOTE_START_X = width - 52;
  const speedPxPerSec = useMemo(
    () => SPEED_BASE * Math.pow(SPEED_GROWTH, level - 1),
    [level]
  );

  const nextNote = () => {
    setTarget((prev) => {
      let n = rand(notesPool);
      while (prev && n.id === prev.id) n = rand(notesPool);
      return n;
    });
  };

  useEffect(() => {
    setTarget(rand(notesPool));
  }, [notesPool]);

  const ledgerYs = useMemo(() => {
    const ys = [];
    const p = target.pos;
    const lowestLine = -1;
    const highestLine = 7;
    if (p < lowestLine) for (let lp = lowestLine - 2; lp >= p; lp -= 2) ys.push(posToY(lp));
    else if (p > highestLine) for (let lp = highestLine + 2; lp <= p; lp += 2) ys.push(posToY(lp));
    return ys;
  }, [target, posToY]);

  const stopRound = () => {
    roundActiveRef.current = false;
  };

  const startGame = () => {
    stopRound();
    setScore(0);
    setStreak(0);
    setLives(3);
    setFlash(null);
    setLevel(1);
    setCorrectCount(0);
    setStatus("running");
    setAnim(null);
    nextNote();
    music.playMusicForLevel(1);
  };

  const pauseGame = () => {
    if (status !== "running") return;
    stopRound();
    setStatus("paused");
    music.stopAllMusic();
  };

  const resumeGame = () => {
    if (status !== "paused") return;
    setStatus("running");
    roundActiveRef.current = true;
    if (!muted) music.playMusicForLevel(level);
  };

  const handleReset = () => {
    stopRound();
    setScore(0);
    setStreak(0);
    setLives(3);
    setFlash(null);
    setLevel(1);
    setCorrectCount(0);
    setStatus("idle");
    setAnim(null);
    music.stopAllMusic();
  };

  useEffect(() => {
    if (status !== "running") return;
    setNoteX(NOTE_START_X);
    roundActiveRef.current = true;
  }, [target, status, NOTE_START_X]);

  useEffect(() => {
    if (status !== "levelup") return;
    const t = setTimeout(() => {
      if (lives > 0) {
        setStatus("running");
        nextNote();
      }
    }, 900);
    return () => clearTimeout(t);
  }, [status, lives]);

  useEffect(() => {
    let last = performance.now();
    const tick = (now) => {
      const dt = (now - last) / 1000;
      last = now;

      if (status === "running" && roundActiveRef.current && noteX !== null) {
        const nx = noteX - speedPxPerSec * dt;
        if (nx <= NOTE_MIN_X) {
          roundActiveRef.current = false;
          setAnim("bonk");
          if (!muted) sfx("wrong");
          setFlash("no");
          setStreak(0);
          setLives((prev) => {
            const nextLives = Math.max(0, prev - 1);
            setTimeout(() => {
              setAnim(null);
              setFlash(null);
              if (nextLives === 0) {
                setStatus("over");
                music.stopAllMusic();
              } else if (status === "running") {
                nextNote();
              }
            }, 320);
            return nextLives;
          });
        } else {
          setNoteX(nx);
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [noteX, muted, status, sfx, speedPxPerSec, NOTE_MIN_X, music]);

  const handleGuess = (name) => {
    if (status !== "running") return;

    const t = targetRef.current;
    if (!t) return;

    if (name === t.name) {
      if (!muted) sfx("correct");
      setScore((s) => s + 10);
      setStreak((s) => s + 1);
      stopRound();
      setFlash("ok");

      setCorrectCount((prev) => {
        const next = prev + 1;
        const willLevelUp = next % LEVEL_INTERVAL === 0;
        setTimeout(() => {
          setFlash(null);
          if (willLevelUp) {
            setLevel((lv) => lv + 1);
            setStatus("levelup");
          } else {
            nextNote();
          }
        }, 250);
        return next;
      });
    } else {
      if (!muted) sfx("wrong");
      setStreak(0);
      setFlash("no");
      setAnim("shake");
      setTimeout(() => setAnim(null), 220);
      setLives((prev) => {
        const nextLives = Math.max(0, prev - 1);
        setTimeout(() => {
          setFlash(null);
          if (nextLives === 0) {
            setStatus("over");
            stopRound();
            music.stopAllMusic();
          }
        }, 250);
        return nextLives;
      });
    }
  };

  useEffect(() => {
    if (status === "running" || status === "levelup") {
      if (!muted) music.playMusicForLevel(level);
    }
  }, [level, status, muted, music]);

  useEffect(() => {
    if (status !== "running" && status !== "levelup") {
      music.stopAllMusic();
      return;
    }
    if (muted) music.stopAllMusic();
    else music.playMusicForLevel(level);
  }, [status, muted, level, music]);

  useEffect(() => {
    if (status !== "running") return;
    const onBridge = (e) => {
      const letter = e?.detail?.letter;
      if (!letter) return;
      const k = String(letter).toUpperCase();
      if (CHOICES.includes(k)) handleGuess(k);
    };
    window.addEventListener(NOTE_KEY_EVENT, onBridge);
    return () => window.removeEventListener(NOTE_KEY_EVENT, onBridge);
  }, [status]);

  return (
    <div className="p-3 sm:p-4">
      <style>{`
        @keyframes note-shake {
          0%{transform:translateX(0)} 25%{transform:translateX(-4px)} 50%{transform:translateX(4px)} 75%{transform:translateX(-3px)} 100%{transform:translateX(0)}
        }
        @keyframes note-bonk {
          0%{transform:translateX(0)} 40%{transform:translateX(6px)} 80%{transform:translateX(-2px)} 100%{transform:translateX(0)}
        }
      `}</style>

      {/* HUD */}
      <div className="mb-3">
        <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:justify-between">
          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:gap-3 text-sm">
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-semibold text-slate-700 whitespace-nowrap">Score</span>
              <span className="px-2 py-0.5 rounded bg-slate-100 border border-slate-200">{score}</span>
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-semibold text-slate-700 whitespace-nowrap">Streak</span>
              <span className="px-2 py-0.5 rounded bg-slate-100 border border-slate-200">{streak}</span>
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-semibold text-slate-700 whitespace-nowrap">Lives</span>
              <span className="px-2 py-0.5 rounded bg-slate-100 border border-slate-200">{lives}</span>
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-semibold text-slate-700 whitespace-nowrap">Level</span>
              <span className="px-2 py-0.5 rounded bg-slate-100 border border-slate-200">{level}</span>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <div className="flex items-center rounded-lg overflow-hidden border border-slate-200 bg-white">
              {[
                { key: "treble", glyph: "𝄞", title: "Treble" },
                { key: "bass", glyph: "𝄢", title: "Bass" },
                { key: "alto", glyph: "𝄡", title: "Alto" },
              ].map(({ key, glyph, title }) => (
                <button
                  key={key}
                  type="button"
                  className={`px-3 py-2 sm:px-2.5 sm:py-1 text-sm font-semibold transition flex items-center justify-center ${
                    clef === key ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
                  }`}
                  onClick={() => {
                    stopRound();
                    setStatus("idle");
                    setClef(key);
                    setScore(0);
                    setStreak(0);
                    setLives(3);
                    setLevel(1);
                    setCorrectCount(0);
                    setFlash(null);
                    setAnim(null);
                    music.stopAllMusic();
                  }}
                  title={title}
                  style={{ lineHeight: 1 }}
                >
                  <span style={{ fontSize: compact ? 28 : 34 }} className="font-[450] leading-none select-none">
                    {glyph}
                  </span>
                </button>
              ))}
            </div>

            {status === "running" && (
              <button
                type="button"
                className="px-2.5 py-2 rounded-lg text-slate-700 hover:bg-slate-100 border border-slate-200 bg-white"
                onClick={pauseGame}
                title="Pause"
                aria-label="Pause"
              >
                <SafeIcon icon={FiPause} className="w-4 h-4" />
              </button>
            )}
            {status === "paused" && (
              <button
                type="button"
                className="px-2.5 py-2 rounded-lg text-slate-700 hover:bg-slate-100 border border-slate-200 bg-white"
                onClick={resumeGame}
                title="Resume"
                aria-label="Resume"
              >
                <SafeIcon icon={FiPlay} className="w-4 h-4" />
              </button>
            )}

            <button
              type="button"
              className="px-2 py-2 rounded-lg text-slate-600 hover:bg-slate-100 border border-slate-200 bg-white"
              onClick={handleReset}
              title="Reset"
              aria-label="Reset"
            >
              <SafeIcon icon={FiRotateCcw} className="w-4 h-4" />
            </button>

            <button
              type="button"
              className="px-2 py-2 rounded-lg text-slate-600 hover:bg-slate-100 border border-slate-200 bg-white"
              onClick={() => setMuted((m) => !m)}
              title={muted ? "Unmute" : "Mute"}
              aria-label={muted ? "Unmute" : "Mute"}
            >
              <SafeIcon icon={muted ? FiVolumeX : FiVolume2} className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Staff + Note */}
      <div className="relative bg-slate-900 rounded-lg p-3 border border-slate-800 overflow-x-auto">
        <div className={compact ? "min-w-[560px]" : "min-w-[720px]"}>
          <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet">
            {[0, 1, 2, 3, 4].map((i) => (
              <line
                key={i}
                x1={40}
                x2={width - 40}
                y1={staffTopY + i * lineGap}
                y2={staffTopY + i * lineGap}
                stroke="#CBD5E1"
                strokeWidth="1.5"
                opacity="0.9"
              />
            ))}

            <text
              x={CLEF_X}
              y={CLEF_Y}
              fontSize={CLEF_FONT_SIZE}
              fill="#E2E8F0"
              fontFamily="Noto Music, Bravura, 'Bravura Text', Petaluma, 'EDU Music', 'Segoe UI Symbol', serif"
              dominantBaseline={clefCfg.baseline || "alphabetic"}
              alignmentBaseline={clefCfg.baseline || "alphabetic"}
              textAnchor="start"
            >
              {clefCfg.glyph}
            </text>

            {ledgerYs.map((y, i) => {
              const nx = noteX ?? NOTE_START_X;
              return (
                <line
                  key={`ledger-${i}`}
                  x1={nx - 26}
                  x2={nx + 26}
                  y1={y}
                  y2={y}
                  stroke="#94A3B8"
                  strokeWidth="1.5"
                />
              );
            })}

            <g
              style={{
                animation:
                  anim === "shake"
                    ? "note-shake 0.22s ease-in-out"
                    : anim === "bonk"
                    ? "note-bonk 0.32s ease-in-out"
                    : "none",
                transformOrigin: `${noteX ?? NOTE_START_X}px ${posToY(target.pos)}px`,
              }}
            >
              <ellipse
                cx={noteX ?? NOTE_START_X}
                cy={posToY(target.pos)}
                rx="9.5"
                ry="7.5"
                transform={`rotate(-20 ${noteX ?? NOTE_START_X} ${posToY(target.pos)})`}
                fill={flash === "ok" ? "#22C55E" : flash === "no" ? "#EF4444" : "#F8FAFC"}
                stroke="#334155"
                strokeWidth="1.5"
              />
            </g>
          </svg>
        </div>

        {/* Game overlays */}
        {(status === "idle" || status === "paused" || status === "over" || status === "levelup") && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900/60">
            <div className="rounded-2xl bg-white/95 backdrop-blur px-6 py-5 border border-slate-200 text-center">
              {status === "idle" && (
                <>
                  <div className="text-sm font-semibold text-slate-600 mb-2">Ready to play?</div>
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-500 active:scale-[0.98] transition"
                    onClick={startGame}
                  >
                    Start
                  </button>
                </>
              )}

              {status === "paused" && (
                <>
                  <div className="text-sm font-semibold text-slate-800 mb-1">Paused</div>
                  <div className="text-xs text-slate-500 mb-3">Tap resume when you’re ready.</div>
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-500 active:scale-[0.98] transition"
                    onClick={resumeGame}
                  >
                    Resume
                  </button>
                </>
              )}

              {status === "over" && (
                <>
                  <div className="text-sm font-semibold text-slate-800 mb-1">Game Over</div>
                  <div className="text-xs text-slate-500 mb-3">
                    Final score: {score} • Level reached: {level}
                  </div>
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg bg-orange-500 text-white font-semibold hover:bg-orange-600 active:scale-[0.98] transition"
                    onClick={startGame}
                  >
                    Play again
                  </button>
                </>
              )}

              {status === "levelup" && (
                <>
                  <div className="text-sm font-semibold text-emerald-700 mb-1">Level up!</div>
                  <div className="text-xs text-slate-600">Welcome to Level {level}</div>
                  <div className="text-[11px] text-slate-500 mt-2">Notes slide a little faster now.</div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Phone portrait rotate prompt (non-blocking; can dismiss) */}
        {showRotateHint && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-slate-950/70">
            <div className="mx-4 w-full max-w-sm rounded-2xl bg-white/95 backdrop-blur border border-slate-200 p-4 text-center shadow-xl">
              <div className="text-base font-semibold text-slate-900">Rotate your phone</div>
              <div className="text-sm text-slate-600 mt-1">
                This game works best in <span className="font-semibold">landscape</span>.
              </div>
              <div className="mt-3 flex items-center justify-center gap-2">
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-slate-900 text-white font-semibold hover:bg-slate-800 active:scale-[0.98] transition"
                  onClick={() => setDismissRotateHint(true)}
                >
                  Continue anyway
                </button>
              </div>
              <div className="mt-2 text-[11px] text-slate-500">
                Tip: you can still switch clef + start/pause from the controls above.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Choices */}
      <div className="mt-3 grid grid-cols-7 gap-2">
        {CHOICES.map((c) => (
          <button
            key={c}
            type="button"
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
            className="py-2 sm:py-2.5 rounded-lg font-semibold bg-white border border-slate-200 hover:bg-slate-50 active:scale-[0.98] transition disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={status !== "running"}
            onClick={() => handleGuess(c)}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="mt-2 text-[11px] text-slate-400 select-none">
        {status !== "running"
          ? `${status === "paused" ? "Paused" : "Stopped"} • Clef: ${clef}`
          : `Clef: ${clef} • Target: ${target.id} • Level ${level} • Speed ${Math.round(
              SPEED_BASE * Math.pow(SPEED_GROWTH, level - 1)
            )}px/s`}
      </div>
    </div>
  );
}